'use client';

import { Tab, Tabs } from '@nextui-org/react';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import ClientOnly from '@/components/client-only';
import { useAuth } from '@/provider/UserProvider';
import type { UserProfile } from '@/types/user';

import Processing from './processing';
import ContentSkeleton from './Skeleton';
import Template from './template';
import BookMark from '@/contents/user-profile/content/bookmark';
import ProfileModel from './models';

export enum ProfileTabKey {
  artwork = 'artwork',
  collection = 'collection',
  processing = 'processing',
  template = 'template',
  bookmarks = 'bookmarks',
  model = 'model',
  apikey = 'apikey',
}

const UserContent: React.FC<{
  user: UserProfile;
}> = ({ user }) => {
  const searchParam = useSearchParams();
  const router = useRouter();
  const tabParam = searchParam?.get('tab') || 'template';
  const [keySelected, setKeySelected] = useState(tabParam);

  useEffect(() => {
    setKeySelected(tabParam);
  }, [tabParam]);

  useEffect(() => {
    router.replace(`/profile/${user?.publicAddress}?tab=${keySelected}`, { scroll: false });
  }, [keySelected]);

  const { auth } = useAuth();
  const isOwner = user.publicAddress === auth.user.publicAddress;

  const LIST_TAB = [
    {
      key: ProfileTabKey.template,
      title: 'Posts',
      component: <Template user={user} />,
      mustOwner: false,
    },
    {
      key: ProfileTabKey.bookmarks,
      title: 'Bookmarks',
      component: <BookMark />,
      mustOwner: true,
    },
    {
      key: ProfileTabKey.processing,
      title: 'Private artwork',
      component: <Processing />,
      mustOwner: true,
    },
    {
      key: ProfileTabKey.model,
      title: 'Models',
      component: <ProfileModel />,
      mustOwner: true,
    },
  ];

  return (
    <div className="main-container">
      <div className="relative mt-7 pb-10">
        <ClientOnly skeleton={<ContentSkeleton />}>
          <>
            <div className="absolute left-0 top-[33px] w-full border-b border-gray-100" />
            <Tabs
              selectedKey={keySelected}
              variant="underlined"
              onSelectionChange={(key) => setKeySelected(key as string)}
              classNames={{
                tabList: 'gap-6 w-full relative rounded-none p-0',
                base: 'sm:w-full overflow-x-[auto]',
                tab: 'p-0 pb-2',
                cursor: 'w-full bg-[#DA613A] h-[4px]',
                tabContent: 'group-data-[selected=true]:text-[#181818] text-[#777e90] font-[600]',
              }}
            >
              {LIST_TAB.filter((item) => !item.mustOwner || item.mustOwner === isOwner).map(
                (tab) => (
                  <Tab key={tab.key} title={tab.title}>
                    {tab.component}
                  </Tab>
                ),
              )}
            </Tabs>
          </>
        </ClientOnly>
      </div>
    </div>
  );
};

export default UserContent;
